/**
 * Auth0 User Profile Model
 *
 * Interfaces for Auth0 Management API user data used for enriching
 * FGA user records with profile information (email, name, etc.).
 *
 * @packageDocumentation
 */

/**
 * Auth0 user profile data retrieved from Management API.
 * Contains only the fields relevant for user enrichment display.
 */
export interface Auth0UserProfile {
  /**
   * Auth0 user identifier in format: {provider}|{user_id}
   * @example "auth0|5457edea1b8f22891a000004"
   * @example "waad|abc123-def456"
   */
  user_id: string;

  /**
   * User's email address
   */
  email?: string;

  /**
   * Whether the email has been verified
   */
  email_verified?: boolean;

  /**
   * Full name of the user
   */
  name?: string;

  /**
   * User's nickname (often the email prefix)
   */
  nickname?: string;

  /**
   * URL to user's profile picture
   */
  picture?: string;

  /**
   * User Principal Name (UPN) - typically same as email for enterprise users
   * Available in user_metadata or app_metadata for some providers
   */
  upn?: string;
}

/**
 * Enriched user combining FGA user ID with Auth0 profile data.
 */
export interface EnrichedUser {
  /**
   * FGA user identifier (full format)
   * @example "user:waad|org_demo|abc123-def456"
   */
  fgaUserId: string;

  /**
   * Auth0 user identifier
   * @example "waad|abc123-def456"
   */
  auth0UserId: string;

  /**
   * Email from Auth0 profile (primary display field)
   */
  email?: string;

  /**
   * Full name from Auth0 profile
   */
  name?: string;

  /**
   * Display value: upn > email > auth0UserId
   */
  displayName: string;

  /**
   * Profile picture URL
   */
  picture?: string;

  /**
   * Whether enrichment has been completed
   */
  enriched: boolean;

  /**
   * Error message if enrichment failed
   */
  error?: string;
}

/**
 * Response from batch user lookup endpoint
 */
export interface Auth0UserBatchResponse {
  /**
   * Map of Auth0 user ID to profile
   */
  users: Record<string, Auth0UserProfile>;

  /**
   * IDs that were not found
   */
  notFound: string[];
}

/**
 * User search result from Auth0
 */
export interface Auth0UserSearchResult {
  /**
   * Auth0 user ID
   */
  user_id: string;

  /**
   * Email address
   */
  email: string;

  /**
   * Full name
   */
  name?: string;

  /**
   * Profile picture URL
   */
  picture?: string;
}

/**
 * Known Auth0 identity providers.
 * These are the providers that use the standard Auth0 user ID format.
 */
const KNOWN_PROVIDERS = ['waad', 'auth0', 'google-oauth2', 'okta', 'github', 'facebook', 'twitter', 'linkedin', 'windowslive', 'samlp', 'adfs', 'oidc'];

/**
 * Converts an FGA user ID to Auth0 user ID format.
 *
 * FGA format: user:{provider}|{org}|{user_id}
 * Auth0 format: {provider}|{org}|{user_id} (just removes the 'user:' prefix)
 *
 * @param fgaUserId - Full FGA user identifier
 * @returns Auth0-compatible user identifier
 * @throws Error if the FGA user ID format is invalid
 *
 * @example
 * ```typescript
 * const auth0Id = fgaToAuth0UserId('user:waad|cinco|2476fb5d-4cf5-4376-9832-a312c2178123');
 * // Returns: 'waad|cinco|2476fb5d-4cf5-4376-9832-a312c2178123'
 * ```
 */
export function fgaToAuth0UserId(fgaUserId: string): string {
  // FGA format: user:{provider}|{org}|{user_id}
  // Auth0 format: {provider}|{org}|{user_id} (same, just without 'user:' prefix)
  const match = fgaUserId.match(/^user:([^|]+)\|(.+)$/);
  if (!match) {
    throw new Error(`Invalid FGA user ID format: ${fgaUserId}`);
  }

  const [, provider, rest] = match;

  // Validate provider is known
  if (!KNOWN_PROVIDERS.includes(provider)) {
    throw new Error(`Invalid FGA user ID format: unknown provider '${provider}'`);
  }

  // Return Auth0 format: provider|org|userId (just strip 'user:' prefix)
  return `${provider}|${rest}`;
}

